import cx from 'classnames';
import type { VoidFunctionComponent } from 'react';

import FromNow from '../../generic/FromNow/FromNow';
import Icon, { icons } from '../../generic/Icon';
import List from '../../generic/List';
import { TruncatedTooltip } from '../../generic/TruncatedTooltip/TruncatedTooltip';
import type {
  FossabotPRAnalysisBadge,
  FossabotPRListItem,
  FossabotPRVcsState,
} from '../../../../shared/fossabot/common';
import type { IconClassName } from '../../generic/Icon';

import { FossabotPrRowActions } from './FossabotPrRowActions';

import styles from './FossabotPrRow.module.less';

type StateIcon = {
  icon: IconClassName;
  /** Resolved CSS-module class colouring the glyph. */
  iconClass: string;
  /**
   * Read out in place of the glyph, which is the row's only signal of state and
   * is hidden from assistive tech like every other Icon.
   */
  label: string;
};

const STATE_ICONS: Record<FossabotPRVcsState, StateIcon> = {
  open: { icon: icons.codePullRequest, iconClass: styles.iconGreen, label: 'Open pull request' },
  draft: { icon: icons.codePullRequestDraft, iconClass: styles.iconGreen, label: 'Draft pull request' },
  closed: { icon: icons.codePullRequestClosed, iconClass: styles.iconRed, label: 'Closed pull request' },
  merged: { icon: icons.codeMerge, iconClass: styles.iconPurple, label: 'Merged pull request' },
};

type AnalysisBadgeConfig = {
  label: string;
  icon?: IconClassName;
  hasSpin?: boolean;
  /** Resolved CSS-module class colouring the icon on its own. */
  iconClass?: string;
  /** Resolved CSS-module class colouring the pill's text and border. */
  badgeClass?: string;
};

/*
 * Hand-rolled rather than built on the shared Badge, which paints text, border
 * and icon in one colour: these pills need a neutral border with the icon
 * carrying the status colour.
 */
const ANALYSIS_BADGES: Record<FossabotPRAnalysisBadge, AnalysisBadgeConfig> = {
  scanning: { label: 'Scanning', icon: icons.spinner, hasSpin: true },
  analyzed: { label: 'Recommended to Merge', icon: icons.check, iconClass: styles.iconGreen },
  needsReview: { label: 'Needs Review', badgeClass: styles.badgeNeedsReview },
};

/*
 * A PR fossabot never analyzed still gets a pill: it echoes the VCS state the
 * way the stat cards do, reusing the state glyph and its colour.
 */
const STATE_BADGES: Record<FossabotPRVcsState, AnalysisBadgeConfig> = {
  open: { ...STATE_ICONS.open, label: 'PR Open' },
  draft: { ...STATE_ICONS.draft, label: 'PR Draft' },
  closed: { ...STATE_ICONS.closed, label: 'PR Closed' },
  merged: { ...STATE_ICONS.merged, label: 'PR Merged' },
};

type Byline = {
  /** Text before the relative time, ending in the space that separates them. */
  prefix: string;
  date: string;
  /** Text after the relative time, starting with its separating space. */
  suffix: string;
};

/**
 * The byline reads from whichever timestamp matches the PR's current state, and
 * drops the initiator when there is no name to show: fossabot opens PRs on a
 * schedule with no user behind them, and a user outside the calling org cannot
 * be resolved either.
 */
const getByline = (pr: FossabotPRListItem): Byline => {
  if (pr.state === 'open' || pr.state === 'draft') {
    return {
      prefix: pr.initiatedByName ? `${pr.initiatedByName} opened ` : 'opened ',
      date: pr.openedAt,
      suffix: ' via fossabot',
    };
  }

  const isMerged = pr.state === 'merged';

  return {
    prefix: isMerged ? 'merged ' : 'closed ',
    //  A closed or merged PR should always carry its timestamp, but the contract allows null.
    date: (isMerged ? pr.mergedAt : pr.closedAt) ?? pr.openedAt,
    suffix: pr.initiatedByName ? ` · initiated by ${pr.initiatedByName} via fossabot` : ' via fossabot',
  };
};

/*
 * A PR has no id of its own on this payload. Its fossabot URL, then its VCS URL,
 * are unique per PR; the last fallback only has to separate rows within one list.
 * Used both as the row's React key and as the id of the title's tooltip.
 */
export const getFossabotPrKey = (pr: FossabotPRListItem): string =>
  pr.fossabotUrl ?? pr.vcsUrl ?? `${pr.state}-${pr.prNumber ?? 'no-number'}-${pr.title}`;

type Props = {
  pr: FossabotPRListItem;
};

/**
 * One PR in the table: state glyph, title, PR number, status badge, a byline
 * and a menu of links out to the PR. The row itself is not clickable, since a PR
 * lives in GitHub and in fossabot rather than anywhere in this app.
 */
export const FossabotPrRow: VoidFunctionComponent<Props> = ({ pr }) => {
  const { icon, iconClass, label } = STATE_ICONS[pr.state];
  /*
   * The analysis pill reads as outstanding work, and there is none left on a
   * merged or closed PR. Those always show their VCS state, and the verdict
   * moves down to a "Last fossabot status" line as history.
   */
  const isFinished = pr.state === 'merged' || pr.state === 'closed';
  const badge = !isFinished && pr.analysisBadge ? ANALYSIS_BADGES[pr.analysisBadge] : STATE_BADGES[pr.state];
  const lastCheck = isFinished && pr.analysisBadge && pr.analysisBadge !== 'scanning'
    ? ANALYSIS_BADGES[pr.analysisBadge].label
    : null;
  const { prefix, date, suffix } = getByline(pr);
  /*
   * Both links in the menu are a URL off this payload, and both can come through
   * missing, so a PR carrying neither has nothing to offer. Left out entirely
   * rather than passed as an empty menu: the right slot is dropped along with it,
   * so the row keeps its own edge instead of holding space for a control that is
   * not there. Blank counts as missing on the same reasoning as the menu itself.
   */
  const hasActions = Boolean(pr.vcsUrl || pr.fossabotUrl);

  return (
    <List.RowContainer>
      <List.Row
        isInteractive={false}
        left={(
          <span className={iconClass}>
            <Icon icon={icon} testId={`fossabot-pr-state-icon-${pr.state}`} />
            <span className="sr-only">{label}</span>
          </span>
        )}
        right={hasActions ? (
          <FossabotPrRowActions
            title={pr.title}
            vcsUrl={pr.vcsUrl}
            fossabotUrl={pr.fossabotUrl}
          />
        ) : null}
      >
        <List.RowTitle>
          <span className={styles.title}>
            {/*
             * PR titles run long and truncate, so the full text needs a way back.
             * TruncatedTooltip opens its tooltip only once the text is actually
             * clipped, and unlike a native `title` it reaches the keyboard too.
             */}
            <TruncatedTooltip
              id={`fossabot-pr-title-${getFossabotPrKey(pr)}`}
              text={pr.title}
              className={styles.name}
            />
            {pr.prNumber !== null && <span className={styles.number}>{`#${pr.prNumber}`}</span>}
            <span className={styles.separator}>·</span>
            <span className={cx(styles.badge, badge.badgeClass)}>
              {badge.icon && (
                <span className={badge.iconClass}>
                  <Icon icon={badge.icon} hasSpin={badge.hasSpin} />
                </span>
              )}
              {badge.label}
            </span>
          </span>
        </List.RowTitle>
        <List.RowSubtitle>
          {prefix}
          <FromNow date={date} />
          {suffix}
        </List.RowSubtitle>
        {lastCheck && <List.RowSubtitle>{`Last fossabot status: ${lastCheck}`}</List.RowSubtitle>}
      </List.Row>
    </List.RowContainer>
  );
};
